import dayjs, { Dayjs } from 'dayjs'

interface EventFormValues {
  title: string
  startDt: Dayjs
  endDt: Dayjs
}

export type EventFormErrorKey = 'calendarEvent.titleRequired' | 'calendarEvent.endBeforeStart'

/**
 * Checks the rendez-vous form before it is sent and returns the translation
 * key of the first problem, or null when it can be saved.
 *
 * A rendez-vous that ends before it starts is hidden from the list by the
 * server, so it must never leave the form. Same start and end is allowed.
 */
export function getEventFormError({ title, startDt, endDt }: EventFormValues): EventFormErrorKey | null {
  if (!title.trim()) return 'calendarEvent.titleRequired'
  if (endDt.isBefore(startDt)) return 'calendarEvent.endBeforeStart'
  return null
}

const atTime = (date: Date | Dayjs, time: string): Dayjs =>
  dayjs(date)
    .hour(parseInt(time.split(':')[0], 10))
    .minute(parseInt(time.split(':')[1], 10))
    .second(0)
    .millisecond(0)

/**
 * A day of the form (start or end date), kept at noon. The hour of a
 * rendez-vous lives in its own field; the date field only says which day.
 *
 * Noon is on purpose: the date pickers hand back the hour of the date they were
 * given, and their confirm handler then shifts it by the time zone (see
 * getPickedDay). From an evening hour that shift crossed midnight and showed
 * the day after the one picked; from noon it cannot.
 */
export const toFormDay = (date: Date | Dayjs): Date =>
  dayjs(date).hour(12).minute(0).second(0).millisecond(0).toDate()

/**
 * Day chosen in a date picker. The time-zone shift is the one the form always
 * applied (the web picker answers with midnight UTC of the chosen day); the
 * result is brought back to noon so that it never adds up from one pick to the
 * next.
 */
export function getPickedDay(
  selectedDate: Date,
  timezoneOffset: number = selectedDate.getTimezoneOffset(),
): Date {
  return toFormDay(new Date(selectedDate.getTime() - timezoneOffset * 60000))
}

interface EventFormEnd {
  endDate: Date
  endTime: string
}

/**
 * Dates and times a new rendez-vous opens with: it starts in one hour and ends
 * one hour later. The end is worked out as a full date and time, so late in
 * the evening it lands on the next day instead of wrapping back before the start.
 */
export function getDefaultEventTimes(now: Dayjs = dayjs()) {
  const start = now.add(1, 'hour')
  const end = start.add(1, 'hour')
  return {
    startDate: toFormDay(start),
    startTime: start.format('HH:mm'),
    endDate: toFormDay(end),
    endTime: end.format('HH:mm'),
  }
}

/**
 * End of the form after the start date was moved to `newStartDate`, or null
 * when the end stays as it is.
 *
 * The end only follows a start moved past it, compared as full dates and
 * times: an end sitting on the next day at 00:30 is passed by a start moved to
 * that day at 23:30. It then takes the same day, unless its hour would put it
 * before the start: in that case it becomes the start + 1 hour, on the next day
 * if need be. For an all-day rendez-vous only the days count.
 */
export function getEndAfterStartDateChange({
  newStartDate,
  startTime,
  endDate,
  endTime,
  allDay,
}: EventFormEnd & { newStartDate: Date; startTime: string; allDay: boolean }): EventFormEnd | null {
  if (allDay) {
    return dayjs(newStartDate).isAfter(endDate, 'day') ? { endDate: newStartDate, endTime } : null
  }
  const startDt = atTime(newStartDate, startTime)
  if (!atTime(endDate, endTime).isBefore(startDt)) return null
  if (!atTime(newStartDate, endTime).isBefore(startDt)) {
    return { endDate: newStartDate, endTime }
  }
  const endDt = startDt.add(1, 'hour')
  return { endDate: toFormDay(endDt), endTime: endDt.format('HH:mm') }
}

/**
 * End an existing rendez-vous opens with in the form. One stored without an
 * end, or (older versions) with an end before its start, opens with an end one
 * hour after its start. Null when it has neither a usable end nor a start.
 */
export function getStoredEventEnd(
  startDateTime?: string | Date | null,
  endDateTime?: string | Date | null,
): Dayjs | null {
  if (startDateTime && (!endDateTime || dayjs(endDateTime).isBefore(startDateTime))) {
    return dayjs(startDateTime).add(1, 'hour')
  }
  return endDateTime ? dayjs(endDateTime) : null
}
